/**
 * MobilePage — "Coming Soon" landing for the iStudyPro mobile app
 * (iter190). Replaces the previous public APK download page.
 *
 * What changed:
 *  - No public APK download. (Avoided "install from unknown sources"
 *    warnings + lets us control rollout via Play Store / TestFlight
 *    when those land.)
 *  - Email waitlist form persists to /api/mobile-waitlist.
 *  - Both iOS and Android shown as "Coming Soon" — sets expectation
 *    that iOS is on the roadmap.
 *  - PWA remains immediately installable (fastest path to "on phone
 *    today"), surfaced as a third option.
 *
 * /download still routes here via App.js redirect to preserve SEO.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Smartphone, Globe, Shield, ArrowLeft, CheckCircle, Plus, Apple, Bell,
  Sparkles, Mail, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import usePwaInstall from '../hooks/usePwaInstall';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobilePage = () => {
  const { canInstall, isStandalone, isIos, promptInstall } = usePwaInstall();
  const [email, setEmail] = useState('');
  const [platform, setPlatform] = useState('both'); // 'ios' | 'android' | 'both'
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/mobile-waitlist/count`)
      .then(({ data }) => setWaitlistCount(data?.total ?? null))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/mobile-waitlist`, {
        email: trimmed,
        source: 'mobile-page',
        platform,
      });
      setJoined(true);
      setWaitlistCount(data?.total_signups ?? waitlistCount);
      toast.success("You're on the list — we'll email you the moment it ships.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't join the waitlist. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      data-testid="mobile-page"
      style={{
        background:
          'radial-gradient(1200px 600px at 20% -10%, rgba(0, 47, 167, 0.20) 0%, rgba(0, 47, 167, 0) 60%),' +
          'radial-gradient(900px 500px at 110% 110%, rgba(232, 224, 251, 0.55) 0%, rgba(232, 224, 251, 0) 60%),' +
          'linear-gradient(180deg, #F5F7FB 0%, #E8EEFB 100%)',
        color: '#0F172A',
      }}
    >
      {/* Decorative blobs to echo the Wave login aesthetic */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -left-32 w-[500px] h-[500px] rounded-full opacity-40"
             style={{ background: 'radial-gradient(circle, rgba(0,47,167,0.22) 0%, transparent 70%)' }} />
        <div className="absolute -bottom-32 -right-32 w-[450px] h-[450px] rounded-full opacity-60"
             style={{ background: 'radial-gradient(circle, rgba(252, 228, 210, 0.6) 0%, transparent 70%)' }} />
      </div>

      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/50 border-b border-slate-200/60">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 h-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="mobile-nav-logo">
            <img src="/logo-main.png" alt="iStudyPro" className="w-8 h-8 object-contain" />
            <span className="text-lg font-bold">
              <span className="text-slate-900">iStudy</span>
              <span className="text-[#002FA7]">Pro</span>
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 transition-colors" data-testid="mobile-back-to-home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </nav>

      <section className="relative z-10 pt-16 pb-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#002FA7]/20 bg-[#002FA7]/8 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#002FA7]" aria-hidden />
            <span className="text-xs text-[#002FA7] font-semibold tracking-wide uppercase">Coming Soon</span>
          </div>

          <h1 className="font-serif-accent text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight mb-4" data-testid="mobile-page-title">
            iStudyPro on your phone,
            <br />
            <span className="text-[#002FA7]">arriving soon.</span>
          </h1>
          <p className="text-base text-slate-600 max-w-xl mx-auto mb-10" data-testid="mobile-page-subtitle">
            Native iOS and Android apps are in the polishing stage. Drop your email and we&apos;ll let you know the moment they hit the stores — no spam, just the launch.
          </p>

          {/* Store badges (visually present, "Coming soon" state) */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <StoreBadge
              icon={Apple}
              storeLine="Coming soon on"
              brandLine="App Store"
              testid="store-badge-ios"
              selected={platform === 'ios'}
              onClick={() => setPlatform('ios')}
            />
            <StoreBadge
              icon={Smartphone}
              storeLine="Coming soon on"
              brandLine="Google Play"
              testid="store-badge-android"
              selected={platform === 'android'}
              onClick={() => setPlatform('android')}
            />
          </div>

          {/* Waitlist form */}
          {!joined ? (
            <form
              onSubmit={submit}
              className="mx-auto max-w-md bg-white/70 backdrop-blur-xl border border-white/60 rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,47,167,0.20)] p-5 sm:p-6"
              data-testid="mobile-waitlist-form"
            >
              <label className="block text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                Join the waitlist
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@university.edu"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/25 focus:border-[#002FA7]/40"
                    data-testid="mobile-waitlist-email"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white font-semibold px-5 py-2.5 rounded-lg gap-2 disabled:opacity-50"
                  data-testid="mobile-waitlist-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  Notify me
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2.5 text-left">
                Free to join. We&apos;ll only email you for the launch.
              </p>
            </form>
          ) : (
            <div
              className="mx-auto max-w-md bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-left"
              data-testid="mobile-waitlist-confirmation"
            >
              <div className="flex items-center gap-2 text-emerald-700 mb-1.5">
                <CheckCircle className="w-5 h-5" aria-hidden />
                <p className="font-bold">You&apos;re on the list.</p>
              </div>
              <p className="text-sm text-emerald-900/80">
                We&apos;ll email <strong>{email}</strong> the moment the {platform === 'ios' ? 'iOS' : platform === 'android' ? 'Android' : 'mobile'} app is ready.
              </p>
            </div>
          )}

          {waitlistCount > 0 && (
            <p className="text-xs text-slate-500 mt-4" data-testid="mobile-waitlist-count">
              <strong>{waitlistCount.toLocaleString()}</strong> student{waitlistCount === 1 ? '' : 's'} already waiting
            </p>
          )}
        </div>
      </section>

      {/* Use it today via PWA */}
      <section className="relative z-10 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-6">
            Don&apos;t want to wait? Use the web app — it works offline.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="p-5 rounded-2xl bg-white/70 backdrop-blur border border-slate-200 text-center">
              <Globe className="w-8 h-8 text-emerald-600 mx-auto mb-3" aria-hidden />
              <h3 className="font-semibold text-slate-900 mb-1">Web App</h3>
              <p className="text-xs text-slate-500 mb-3">Full access from any browser</p>
              <Link to="/app">
                <Button size="sm" className="rounded-xl bg-[#002FA7] hover:bg-[#00227A] text-white gap-1.5 w-full" data-testid="open-web-btn">
                  <CheckCircle className="w-3.5 h-3.5" /> Open web app
                </Button>
              </Link>
            </div>
            <div className="p-5 rounded-2xl bg-white/70 backdrop-blur border border-slate-200 text-center">
              <Smartphone className="w-8 h-8 text-[#002FA7] mx-auto mb-3" aria-hidden />
              <h3 className="font-semibold text-slate-900 mb-1">Install as PWA</h3>
              <p className="text-xs text-slate-500 mb-3">Adds to your home screen — works offline</p>
              {isStandalone ? (
                <Button size="sm" disabled className="w-full gap-1.5 opacity-60" data-testid="pwa-installed-btn">
                  <CheckCircle className="w-3.5 h-3.5" /> Already installed
                </Button>
              ) : canInstall ? (
                <Button
                  size="sm"
                  onClick={async () => {
                    const r = await promptInstall();
                    if (r?.outcome === 'accepted') toast.success('Installing iStudyPro…');
                  }}
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white w-full gap-1.5"
                  data-testid="pwa-install-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Install
                </Button>
              ) : isIos ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toast.info('Tap the Share icon, then "Add to Home Screen"', { duration: 5000 })}
                  className="w-full gap-1.5"
                  data-testid="pwa-ios-btn"
                >
                  How to install on iOS
                </Button>
              ) : (
                <Button size="sm" variant="outline" disabled className="w-full gap-1.5 opacity-60" data-testid="pwa-unavailable-btn">
                  Use browser menu →
                </Button>
              )}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#002FA7]" aria-hidden /> Secure &amp; Private</span>
            <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-amber-500" aria-hidden /> Built for students</span>
            <span className="flex items-center gap-1.5"><Bell className="w-3.5 h-3.5 text-emerald-600" aria-hidden /> No spam, ever</span>
          </div>
        </div>
      </section>
    </div>
  );
};

const StoreBadge = ({ icon: Icon, storeLine, brandLine, selected, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-3 px-5 py-3 rounded-2xl border transition-all text-left ${
      selected
        ? 'bg-[#002FA7] text-white border-[#002FA7] shadow-lg shadow-[#002FA7]/25'
        : 'bg-white/70 text-slate-900 border-slate-200 hover:border-[#002FA7]/40'
    }`}
    aria-pressed={selected}
    data-testid={testid}
  >
    <Icon className="w-7 h-7 flex-shrink-0" aria-hidden />
    <div>
      <p className={`text-[10px] uppercase tracking-[0.14em] ${selected ? 'opacity-80' : 'text-slate-500'}`}>{storeLine}</p>
      <p className="font-bold leading-tight">{brandLine}</p>
    </div>
  </button>
);

export default MobilePage;
